import { api, auth, refreshAccessToken } from './client';
import type { CurrentUser, LoginResponse, MfaEnrollment, MfaStatus, PublicTenant } from './types';

export const tenantApi = {
  /**
   * Identity and branding of the clinic on this domain.
   *
   * One bundle is served on every clinic domain, so this is how the app learns
   * which clinic it is rendering. Called before anything else.
   */
  current: () => api.get<PublicTenant>('/tenant', { skipAuth: true }),
};

/**
 * Starea verificării în doi pași, cu câte coduri de rezervă au mai rămas.
 *
 * Numărul e ce împiedică omul să afle că nu mai are niciunul în clipa în care
 * și-a pierdut telefonul (SEC-5).
 */
export interface MfaStatusWithCodes extends MfaStatus {
  recoveryCodesLeft: number;
}

/**
 * Codul de la al doilea pas: ori șase cifre din aplicație, ori un cod de
 * rezervă.
 *
 * Se despart aici, după forma lor, ca formularul de autentificare să aibă un
 * singur câmp de trimis și ca `session.login` să nu capete un al patrulea
 * parametru pe care îl pasează mai departe neatins.
 */
function secondStep(code: string | undefined): Record<string, string> {
  if (!code) return {};
  return /^\d{6}$/.test(code.trim()) ? { mfaCode: code.trim() } : { recoveryCode: code.trim() };
}

/** O sesiune: o autentificare, reînnoită de browser cât timp e folosită. */
export interface SessionView {
  id: string;
  startedAt: string;
  lastSeenAt: string;
  expiresAt: string;
  userAgent: string | null;
  ip: string | null;
  current: boolean;
}

export type SignInOutcome =
  | 'ok'
  | 'ok_recovery'
  | 'wrong_password'
  | 'wrong_code'
  | 'wrong_recovery_code'
  | 'locked'
  | 'disabled';

export interface SignInView {
  at: string;
  outcome: SignInOutcome;
  ip: string | null;
  userAgent: string | null;
}

export interface LookPrefs {
  theme?: 'light' | 'dark';
  appearance?: 'standard' | 'cabinet' | 'studio';
  calStyle?: 'compact' | 'columns' | 'full';
  /** Secțiunile scoase din meniul acestui om — o vedere, nu un drept. */
  menuHidden?: string[];
  /** Indiciile de ecran pe care le-a citit și le-a închis. */
  hintsSeen?: string[];
}

/** Ce primește `POST /auth/look` — exact `LookPrefsDto`. */
export interface LookPrefsInput {
  theme?: 'light' | 'dark' | null;
  appearance?: 'standard' | 'cabinet' | 'studio' | null;
  calStyle?: 'compact' | 'columns' | 'full' | null;
  /** Lista întreagă a secțiunilor ascunse; `null` sau goală pune meniul la loc. */
  menuHidden?: string[] | null;
  /** Lista întreagă a indiciilor citite; goală le aduce pe toate înapoi. */
  hintsSeen?: string[] | null;
}

export const authApi = {
  async login(email: string, password: string, mfaCode?: string): Promise<LoginResponse> {
    const result = await api.post<LoginResponse>(
      '/auth/login',
      { email, password, ...secondStep(mfaCode) },
      { skipAuth: true },
    );
    auth.setAccessToken(result.accessToken);
    auth.setRefreshToken(result.refreshToken);
    return result;
  },

  me: () => api.get<CurrentUser>('/auth/me'),

  /**
   * Invitația din link — cine e, ca pagina să spună un nume.
   *
   * `skipAuth`: se deschide fără cont, fiindcă exact asta e rostul ei.
   */
  invitation: (token: string) =>
    api.get<{ email: string; name: string | null }>(
      `/auth/invitation/${encodeURIComponent(token)}`,
      { skipAuth: true },
    ),

  acceptInvitation: (token: string, password: string) =>
    api.post<void>(
      `/auth/invitation/${encodeURIComponent(token)}`,
      { password },
      { skipAuth: true },
    ),

  async logout(allDevices = false): Promise<void> {
    const refreshToken = auth.getRefreshToken() ?? undefined;
    try {
      await api.post<void>(`/auth/logout${allDevices ? '?allDevices=true' : ''}`, { refreshToken });
    } finally {
      // Cleared even if the call fails: the user asked to sign out, and
      // leaving the token behind because the network was down is not an
      // acceptable answer.
      auth.clear();
    }
  },

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<void>('/auth/password', { currentPassword, newPassword }),

  /** Set my own preferred interface language; `null` follows the clinic default. */
  setLanguage: (language: string | null) => api.post<void>('/auth/language', { language }),

  /** Aspectul ales de om pentru el (Setări → Aspectul meu); o cheie lipsă urmează clinica. */
  lookPrefs: () => api.get<LookPrefs>('/auth/look'),
  /** O schimbare: câmpul lipsă rămâne, `null` revine la alegerea clinicii. */
  setLookPrefs: (change: LookPrefsInput) => api.post<LookPrefs>('/auth/look', change),

  mfaStatus: () => api.get<MfaStatusWithCodes>('/auth/mfa'),
  mfaEnroll: () => api.post<MfaEnrollment>('/auth/mfa/enroll'),
  /** Confirmarea înrolării întoarce codurile de rezervă — o singură dată. */
  mfaVerify: (code: string) => api.post<{ recoveryCodes: string[] }>('/auth/mfa/verify', { code }),
  mfaDisable: (password: string) => api.post<void>('/auth/mfa/disable', { password }),
  /** Zece coduri noi, în locul celor rămase. Arătate o dată, ca la înrolare. */
  mfaRecoveryCodes: (password: string) =>
    api.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', { password }),

  /** Unde e deschis contul (SEC-16); sesiunea de acum e marcată. */
  sessions: () => api.get<SessionView[]>('/auth/sessions'),
  endSession: (id: string) => api.delete<void>(`/auth/sessions/${id}`),
  /** Ultimele încercări de autentificare, reușite sau nu. */
  signIns: () => api.get<SignInView[]>('/auth/sign-ins'),
};

/**
 * Restore a session on page load.
 *
 * The access token lives in memory only, so a reload always starts without
 * one. If a refresh token survived, exchange it; otherwise the user signs in.
 */
export async function restoreSession(): Promise<CurrentUser | null> {
  if (!auth.getRefreshToken()) return null;
  try {
    // The access token lives in memory, so after a reload there is none and
    // the first authenticated call is certain to 401. Relying on the client's
    // refresh-on-401 path works, but it spends a round trip on a request that
    // cannot succeed and puts a red 401 in the console on every single load —
    // which is how a real one goes unnoticed. Refresh first when we know we
    // have to; the 401 path stays for the token that expires mid-session.
    if (!auth.getAccessToken() && !(await refreshAccessToken())) {
      auth.clear();
      return null;
    }
    return await authApi.me();
  } catch {
    auth.clear();
    return null;
  }
}
